import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import { IConversationLine } from '@datatype/iyagigo/api-type';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import { BOTTOM_FADE_IN_CSS } from '@constant/animation';
import { normalizeExplanation, replaceDashWithTilde } from '@util/string';

interface IProps {
  line?: IConversationLine;
  audioLink: string | null;
  isExpanded: boolean;
  isMyLine: boolean;
  handleExplanationClick: () => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const ControlRow = styled.div<{ $isMyLine: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: ${(props) => (props.$isMyLine ? 'flex-end' : 'flex-start')};
  gap: ${size.MARGIN_1};
  margin: 5px 0 0 0;
`;

const ExplanationContainer = styled.div<{ $isMyLine: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: 4px;
  padding: ${size.MARGIN_1};
  background-color: ${(props) => (props.$isMyLine ? color.WHITE_1 : color.WHITE_1)};
  border: ${size.BORDER_WIDTH_1} solid ${(props) => (props.$isMyLine ? color.BROWN_2 : color.GRAY_1)};
  border-radius: ${size.BORDER_RADIUS_1};
  ${BOTTOM_FADE_IN_CSS}
`;

const AudioContainer = styled.div`
  display: none;
`;

const LineControl: React.FC<IProps> = ({ line, audioLink, isExpanded, isMyLine, handleExplanationClick }) => {
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick } = useAudioPlayer({
    sources: audioLink ? [audioLink] : [],
  });

  return (
    <Wrapper>
      <ControlRow $isMyLine={isMyLine}>
        {audioLink && (
          <>
            <IconBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handlePlayAndPauseClick }}
            />
            <AudioContainer>
              <audio
                ref={(element) => {
                  if (element) audioRefs.current[0] = element;
                }}
                src={audioLink}
                onEnded={handlePlayCompleted}
                controls={false}
              />
            </AudioContainer>
          </>
        )}
        {(line?.translation || line?.explanation) && (
          <IconBlock
            icon={isExpanded ? 'text-snippet-off' : 'text-snippet'}
            design={{
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleExplanationClick }}
          />
        )}
      </ControlRow>
      {isExpanded && (
        <ExplanationContainer $isMyLine={isMyLine}>
          {line?.translation && (
            <TextBlock
              text={normalizeExplanation(replaceDashWithTilde(line.translation), color.BLACK_2)}
              design={{
                text: { size: 'small', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          )}
          {line?.explanation && (
            <>
              <Gap height={'5px'} />
              <TextBlock
                text={normalizeExplanation(replaceDashWithTilde(line.explanation), color.GRAY_1)}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false },
                  box: { alignX: 'left' },
                }}
              />
            </>
          )}
        </ExplanationContainer>
      )}
    </Wrapper>
  );
};

export default LineControl;
